import React, {
  lazy,
  Suspense,
  useEffect,
  useMemo,
  useState,
  MouseEvent,
} from "react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import BrowserUpdatedIcon from "@mui/icons-material/BrowserUpdated";
import HistoryIcon from "@mui/icons-material/History";
import Form from "react-bootstrap/Form";
import "../../Components/SharedComponents/InputBox.scss";
import {
  MaterialReactTable,
  type MRT_ColumnDef,
  MRT_ColumnFiltersState,
  type MRT_Row,
  type MRT_TableOptions,
  useMaterialReactTable,
  MRT_ToggleFiltersButton,
  MRT_ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFullScreenButton,
} from "material-react-table";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import {
  QueryClient,
  QueryClientProvider,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { type User } from "./makeData";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Accordion from "react-bootstrap/Accordion";
import { Container, Row } from "reactstrap";
import axiosInstance from "../../Utils/axiosInstance";
import { backendbaseUrl } from "../../Utils/baseUrl";
import { toast } from "react-toastify";
import styled from "styled-components";
import {
  showDeleteConfirmModal,
  ResetTablePreferencesButton,
} from "../../Components/ConfirmModal";
import { CompactTextField } from "../../Components/SharedComponents/SharedComponents";
import FileUpload from "../../Components/FileUpload/FileUpload";
import { useNavigate } from "react-router-dom";
import { useTablePersistence } from "../../hooks/useTablePersistence";
import { TableActionVisibilityMenu } from "../../Components/SharedComponents/TableActionVisibilityMenu";
import { SortableActionButton } from "../../Components/SharedComponents/SortableActionButton";
import { TableColumnVisibilityMenu } from "../../Components/SharedComponents/TableColumnVisibilityMenu";

export const StyledTableFirstSecond = styled.div`
  thead tr:first-child th:first-child {
    width: 4% !important;
  }
  thead tr:first-child th:nth-child(2) {
    width: 1% !important;
  }
`;

const Example = () => {
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string | undefined>
  >({});
  const {
    state: tableState,
    resetState: resetTableState,
    setColumnFilters,
    setColumnVisibility,
    setColumnOrder,
    setColumnPinning,
    setColumnSizing,
    setDensity,
    setGlobalFilter,
    setShowColumnFilters,
    setShowGlobalFilter,
    setSorting,
    setPagination,
    setGrouping,
    setActionButtonOrder,
    setActionButtonVisibility,
    setIsFullScreen,
  } = useTablePersistence("derivative_table", {
    density: "comfortable",
    pagination: { pageIndex: 0, pageSize: 15 },
    columnFilters: [],
    columnVisibility: {},
    columnOrder: [],
    columnPinning: {},
    columnSizing: {},
    sorting: [],
    grouping: [],
    globalFilter: "",
    showColumnFilters: true,
    showGlobalFilter: true,
    actionButtonOrder: [],
    actionButtonVisibility: {},
    isFullScreen: false,
  });

  const columnFilters = tableState.columnFilters || [];
  const columnVisibility = tableState.columnVisibility || {};
  const density = tableState.density || "comfortable";
  const globalFilter = tableState.globalFilter;
  const showGlobalFilter = tableState.showGlobalFilter ?? true;
  const showColumnFilters = tableState.showColumnFilters ?? true;
  const sorting = tableState.sorting || [];
  const pagination = tableState.pagination || { pageIndex: 0, pageSize: 15 };
  const columnOrder = tableState.columnOrder || [];
  const columnPinning = tableState.columnPinning || {};
  const columnSizing = tableState.columnSizing || {};
  const grouping = tableState.grouping || [];
  const actionButtonOrder = tableState.actionButtonOrder || [];
  const actionButtonVisibility = tableState.actionButtonVisibility || {};
  const isFullScreen = tableState.isFullScreen ?? false;

  const defaultButtonOrder = [
    "upload",
    "export_all",
    "history",
    "export_selected",
    "delete_selected",
    "reset"
  ];

  const buttonsConfig: Record<string, { label: string, tooltipText: string, description: string }> = {
    "upload": { label: "Upload File", tooltipText: "Upload new template \u2022 Drag to reorder", description: "Upload new template" },
    "export_all": { label: "Export All Data", tooltipText: "Export all records \u2022 Drag to reorder", description: "Export all records" },
    "history": { label: "History Log", tooltipText: "View activity history \u2022 Drag to reorder", description: "View activity history" },
    "export_selected": { label: "Export Selected Rows", tooltipText: "Export selected \u2022 Drag to reorder", description: "Export selected" },
    "delete_selected": { label: "Delete Selected", tooltipText: "Delete selected \u2022 Drag to reorder", description: "Delete selected" },
    "reset": { label: "Reset View", tooltipText: "Reset table preferences \u2022 Drag to reorder", description: "Reset table preferences" }
  };

  const currentActionButtonOrder = actionButtonOrder.length > 0 ? actionButtonOrder : defaultButtonOrder;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = currentActionButtonOrder.indexOf(active.id as string);
      const newIndex = currentActionButtonOrder.indexOf(over.id as string);
      const newOrder = arrayMove(currentActionButtonOrder, oldIndex, newIndex);
      setActionButtonOrder(newOrder);
    }
  };

  const [filterModes, setFilterModes] = useState<
    { mode: string; id: string; value: unknown }[]
  >([]);

  const [derivativeName, setDerivativeName] = useState<string>("");
  const [inputBackgroundColour, setInputBackgroundColour] = useState("");
  const [showRowResizing, setShowRowResizing] = useState<boolean>(true);
  const [totalCount, setTotalCount] = useState(0);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({
    derivativeName: "",
  });
  const [touched, setTouched] = useState<Record<string, boolean>>({
    derivativeName: false,
  });

  const [openFileUpload, setOpenFileUpload] = useState<boolean>(false);
  const handleFileUploadOpen = () => setOpenFileUpload(true);
  const handleFileUploadClose = () => setOpenFileUpload(false);

  const handleFileUploadSubmit = async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axiosInstance.post(
        `${backendbaseUrl}/paper-app/derivatives/`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      queryClient.invalidateQueries({ queryKey: ["users"] });

      return {
        success: true,
        message: "File uploaded and data added successfully!",
        data: response.data,
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || "Failed to upload file",
      };
    }
  };

  const validateField = (field: string, value: string): string | null => {
    if (field === "derivativeName" && !validateRequired(value)) {
      return "Derivative Name is Required";
    }
    return null;
  };

  const handleFocus = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const validateOnBlur = (value: string): string | null => {
    const error = validateField("derivativeName", value);
    setFormErrors((prev) => ({ ...prev, derivativeName: error || "" }));
    return error;
  };

  const columns = useMemo<MRT_ColumnDef<User>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Derivative",
        enableEditing: true,
        muiTableBodyCellProps: ({ row, table }) => ({
          onDoubleClick: () => {
            table.setEditingRow(row);
          },
          sx: {
            height: "10px",
            cursor: "pointer",
            "&:hover": {
              backgroundColor: "rgba(44, 4, 4, 0.05)",
            },
            ...(table.getState().editingRow?.id === row.id && {
              backgroundColor: "rgba(0, 128, 0, 0.1)",
              transition: "background-color 0.3s ease",
            }),
          },
        }),
        muiEditTextFieldProps: {
          name: "name",
          required: true,
          error: !!validationErrors?.name,
          helperText: validationErrors?.name,
          onFocus: () =>
            setValidationErrors((prev) => ({
              ...prev,
              name: undefined,
            })),
        },
      },
    ],
    [validationErrors]
  );

  const { mutateAsync: createUser, isPending: isCreatingUser } =
    useCreateUser();
  const {
    data,
    isError: isLoadingUsersError,
    isFetching: isFetchingUsers,
    isLoading: isLoadingUsers,
  } = useGetData(filterModes, pagination, setTotalCount, sorting);
  const { mutateAsync: updateUser, isPending: isUpdatingUser } =
    useUpdateUser();
  const { mutateAsync: deleteUser, isPending: isDeletingUser } =
    useDeleteUser();

  const createDerivative = async (derivative: { id: string; name: string }) => {
    if (!derivative.name) {
      toast.error("Derivative name is required!");
      return;
    }

    try {
      await axiosInstance.post(
        `${backendbaseUrl}/paper-app/derivatives/`,
        derivative
      );

      queryClient.invalidateQueries({ queryKey: ["users"] });

      toast.success("Derivative added successfully!");
    } catch (error: any) {
      console.error("Error adding derivative:", error);

      let message = "Failed to add derivative. Please try again.";

      if (error.response?.data) {
        const apiErrors = error.response.data;

        // Case 1: Backend sends {"name": ["..."]}
        if (apiErrors.name && Array.isArray(apiErrors.name)) {
          message = apiErrors.name[0];
        }
        // Case 2: Backend sends { "error": "Something went wrong: {...}" }
        else if (typeof apiErrors.error === "string") {
          const match = apiErrors.error.match(/ErrorDetail\(string='(.+?)'/);
          if (match && match[1]) {
            message = match[1]; // ✅ Extract only the clean message
          } else {
            message = apiErrors.error; // fallback if parsing fails
          }
        }
      }

      toast.error(message);
    }
  };

  const formCreate = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const derivative = {
      id: Date.now().toString(),
      name: derivativeName.trim(),
    };

    await createDerivative(derivative);
    setDerivativeName("");
  };

  const handleCreateUser: MRT_TableOptions<User>["onCreatingRowSave"] = async ({
    values,
    table,
  }) => {
    const newValidationErrors = validateUser(values);
    if (Object.values(newValidationErrors).some((error) => error)) {
      setValidationErrors(newValidationErrors);
      return;
    }

    setValidationErrors({});

    const derivative = {
      id: Date.now().toString(),
      name: values.name.trim(),
    };

    await createDerivative(derivative);
    table.setCreatingRow(null);
  };

  // const handleeditsave: MRT_TableOptions<User>["onEditingRowSave"] = async ({ values, table, row }) => {
  //   console.log("Saving User - Full Row Original Data:", row.original);
  //   console.log("Saving User - Edited Values:", values);

  //   const updatedUser = {
  //     ...row.original,
  //     ...values,
  //   };

  //   console.log("Saving User - Merged User Data:", updatedUser);

  //   const newValidationErrors = validateUser(updatedUser);
  //   if (Object.values(newValidationErrors).some((error) => error)) {
  //     console.log("Validation Errors:", newValidationErrors);
  //     setValidationErrors(newValidationErrors);
  //     return;
  //   }

  //   setValidationErrors({});

  //   try {
  //     await updateUser(updatedUser);
  //     table.setEditingRow(null);
  //   } catch (error) {
  //     console.error("User Update Error:", error);
  //   }
  // };
  const handleEditSave: MRT_TableOptions<User>["onEditingRowSave"] = async ({
    values,
    table,
    row,
  }) => {
    console.log("Saving User - Full Row Original Data:", row.original);
    console.log("Saving User - Edited Values:", values);

    const updatedUser = {
      ...row.original,
      ...values,
    };

    console.log("Saving User - Merged User Data:", updatedUser);

    // 🔎 Check for empty fields
    for (const [key, value] of Object.entries(updatedUser)) {
      if (
        value === null ||
        value === undefined ||
        value.toString().trim() === ""
      ) {
        toast.warning(`feild cannot be blank!` as string); // ✅ cast
        return;
      }
    }

    // ✅ Run your existing validation
    const newValidationErrors = validateUser(updatedUser);
    if (Object.values(newValidationErrors).some((error) => error)) {
      console.log("Validation Errors:", newValidationErrors);
      setValidationErrors(newValidationErrors);

      const firstError = Object.values(newValidationErrors).find((err) => err);
      toast.error(
        (firstError as string) || "Please fix validation errors before saving"
      ); // ✅ cast to string
      return;
    }

    setValidationErrors({});

    try {
      await updateUser(updatedUser);
      table.setEditingRow(null);
      toast.success("Derivative updated successfully");
    } catch (error) {
      console.error("Derivative Update Error:", error);
      toast.error("Failed to update Derivative");
    }
  };

  const openDeleteConfirmModal = (row: MRT_Row<User>) => {
    showDeleteConfirmModal({
      title: "Are you sure you want to delete this derivative?",
      content: `Deleting ... `,
      onOk: () => {
        deleteUser(row.original.id)
          .then(() => {
            toast.success("Derivative deleted successfully!");
          })
          .catch((error) => {
            toast.error("Failed to delete derivative. Please try again.");
          });
      },
    });
  };

  const handleCopyRow = (row: any) => {
    if (!row) return;
    const rowData = row.original;
    let textToCopy = JSON.stringify(rowData, null, 2);
    navigator.clipboard.writeText(textToCopy);

    let jsonData;

    try {
      jsonData = JSON.parse(textToCopy);
      setDerivativeName(jsonData.name);
      window.scrollTo({ top: 0, behavior: "smooth" });
      toast.success("copied and pasted successfully");
      setInputBackgroundColour("#83bf6e26");
      setTimeout(() => {
        setInputBackgroundColour("");
      }, 2400);
    } catch (error) {
      console.error("Invalid JSON. Pasted as a string:", textToCopy);
      jsonData = { rawText: textToCopy };
    }
  };

  interface ContextMenuPosition {
    mouseX: number;
    mouseY: number;
  }

  const [contextMenu, setContextMenu] = useState<ContextMenuPosition | null>(
    null
  );

  const handleContextMenu = (event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    setContextMenu(
      contextMenu === null
        ? { mouseX: event.clientX + 2, mouseY: event.clientY - 6 }
        : null
    );
  };

  const handleClose = () => {
    setContextMenu(null);
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      let jsonData;

      try {
        jsonData = JSON.parse(text);
        setDerivativeName(jsonData.name);
        window.scrollTo({ top: 0, behavior: "smooth" });
        toast.success("copied and pasted successfully");
        setInputBackgroundColour("#83bf6e26");
        setTimeout(() => {
          setInputBackgroundColour("");
        }, 2400);
      } catch (error) {
        console.error("Invalid JSON. Pasted as a string:", text);
        jsonData = { rawText: text };
      }
    } catch (err) {
      console.error("Failed to read clipboard contents:", err);
    }

    handleClose();
  };

  const handleResizing = () => {
    setShowRowResizing(!showRowResizing);
    handleClose();
  };

  const handleExportRows = (rows: MRT_Row<any>[]) => {
    const rowData = rows.map((row) => row.original);
    console.log(rowData);
  };

  const handleDataDownload = async () => {
    try {
      const response = await axiosInstance.get(
        `${backendbaseUrl}/paper-app/derivatives/export-delete`,
        {
          responseType: "blob",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.data) {
        throw new Error("No data received from server");
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "all_derivatives.csv");
      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

      toast.success("All data download started");
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download all data");
    }
  };

  const navigate = useNavigate();
  const handlehistory = () => {
    navigate("/settings/log/history/derivative");
  };

  const handleBulkDelete = async (rows: MRT_Row<any>[]) => {
    const rowIds = rows.map((row) => row.original.id);

    if (rowIds.length === 0) {
      toast.warn("Please select at least one derivative to delete.");
      return;
    }

    console.log("Selected IDs for deletion:", rowIds);

    try {
      showDeleteConfirmModal({
        title: `Delete ${rowIds.length} selected derivative(s)?`,
        content: `This will permanently delete ${rowIds.length} derivative(s). Continue?`,
        onOk: async () => {
          const loadingToast = toast.loading(
            `Deleting ${rowIds.length} derivative(s)...`
          );
          const payload = {
            ids: rowIds.filter((id) => id),
          };
          console.log(payload);
          try {
            const payload = {
              ids: rowIds.filter((id) => id),
            };

            console.log("Sending delete payload:", payload);

            const response = await axiosInstance.delete(
              `${backendbaseUrl}/paper-app/derivatives/export-delete/`,
              {
                data: payload,
                headers: {
                  "Content-Type": "application/json",
                  "X-Requested-With": "XMLHttpRequest",
                },
              }
            );

            if (response.status === 200) {
              toast.success(
                `Successfully deleted ${rowIds.length} derivative(s)`
              );
              queryClient.invalidateQueries({ queryKey: ["users"] });
              table.setRowSelection({});
            } else {
              throw new Error(
                response.data?.message || "Unknown error occurred"
              );
            }
          } catch (error: any) {
            console.error("Delete error details:", {
              message: error.message,
              response: error.response?.data,
              status: error.response?.status,
            });

            const errorMessage =
              error.response?.data?.detail ||
              error.response?.data?.message ||
              error.message ||
              "Failed to delete derivatives";

            toast.error(`Deletion failed: ${errorMessage}`);
          } finally {
            toast.dismiss(loadingToast);
          }
        },
      });
    } catch (error) {
      console.error("Confirmation dialog error:", error);
      toast.error("An error occurred during the deletion process.");
    }
  };

  const openrowhistory = (row: any) => {
    navigate(`/settings/log/history/derivative/${row.id}`);
  };

  const table = useMaterialReactTable({
    columns,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableClickToCopy: true,
    enableKeyboardShortcuts: true,
    enableColumnActions: true,
    enableColumnDragging: true,
    enableGlobalFilter: false,
    enableGrouping: true,
    enableHiding: true,
    enableSorting: true,
    enableColumnPinning: true,
    onPaginationChange: setPagination,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onDensityChange: setDensity,
    onGlobalFilterChange: setGlobalFilter,
    onShowColumnFiltersChange: setShowColumnFilters,
    onShowGlobalFilterChange: setShowGlobalFilter,
    onIsFullScreenChange: setIsFullScreen,
    onColumnOrderChange: setColumnOrder,
    onColumnPinningChange: setColumnPinning,
    onColumnSizingChange: setColumnSizing,
    onSortingChange: setSorting,
    onGroupingChange: setGrouping,
    manualSorting: true,
    manualPagination: true,
    enableRowSelection: true,
    // positionToolbarAlertBanner: "bottom",
    enableColumnResizing: true,
    // columnResizeMode: "onEnd",
    // defaultColumn: {
    //   minSize: 50,
    //   enableResizing: true,
    // },
    enableFilterMatchHighlighting: true,
    rowCount: totalCount,
    manualFiltering: true,
    data: data?.results || [],
    enableDensityToggle: true,
    createDisplayMode: "row",
    editDisplayMode: "row",
    enableEditing: true,
    muiTopToolbarProps: ({ table }) => ({
      sx: {
        backgroundColor: "#fff",
        padding: "8px",
        "& .MuiBox-root.css-wsew38": {
          backgroundColor: "#fff",
          display: "flex",
          alignItems: "center",
          gap: "8px",
        },
      },
      className: "top-toolbar-wrapper__new",
    }),
    muiTableContainerProps: ({ table }) => ({
      sx: {
        backgroundColor: "#fff",
      },
      className: "table-container-wrapper__new",
    }),
    muiTableFooterProps: ({ table }) => ({
      sx: {
        backgroundColor: "#fff",
      },
      className: "table-footer-wrapper__new",
    }),
    muiBottomToolbarProps: ({ table }) => ({
      sx: {
        backgroundColor: "#fff",
        padding: "8px",
      },
      className: "bottom-toolbar-wrapper__new",
    }),
    muiTableBodyRowProps: ({ row, table }) => ({
      onKeyDown: (event: React.KeyboardEvent<HTMLTableRowElement>) => {
        if (event.key === "Enter") {
          event.preventDefault();
          const editingRow = table.getState().editingRow;
          if (editingRow) {
            const saveHandler = table.options.onEditingRowSave;
            if (saveHandler) {
              const newedit = {
                ...editingRow.original,
                ...editingRow._valuesCache,
              };
              console.log("Saving:", newedit);
              saveHandler({
                exitEditingMode: () => table.setEditingRow(null),
                row: editingRow,
                table,
                values: newedit,
              });
            }
          }
        }
      },
    }),
    enableColumnOrdering: true,
    enableRowOrdering: false,
    getRowId: (row) => row.id,
    enableColumnFilterModes: true,
    // initialState: { showColumnFilters: true },
    filterFns: {
      customFilterFn: (row, id, filterValue) => {
        return row.getValue(id) === filterValue;
      },
    },
    // defaultDisplayColumn: {
    //   enableResizing: true,
    // },
    displayColumnDefOptions: {
      "mrt-row-actions": {
        enableColumnActions: true,
        size: 200,
        muiTableHeadCellProps: {
          sx: { minWidth: "200px" }, // ✅ enforce min width
        },
        muiTableBodyCellProps: {
          sx: { minWidth: "200px" },
        },
      },
      "mrt-row-numbers": {
        enableColumnDragging: true,
        enableColumnOrdering: true,
        enableResizing: true,
        muiTableHeadCellProps: {
          sx: {
            fontSize: "1.2rem",
          },
        },
      },
      "mrt-row-select": {
        enableColumnActions: true,
        enableHiding: true,
        //size: 50,
      },
    },
    renderTopToolbarCustomActions: ({ table }) => {
      const hasSelection = table.getIsSomeRowsSelected() || table.getIsAllRowsSelected();

      const buttonsMap: Record<string, React.ReactNode> = {
        "upload": (
          <Button startIcon={<UploadFileIcon />} onClick={handleFileUploadOpen}>
            Upload File
          </Button>
        ),
        "export_all": (
          <Button
            onClick={() => handleDataDownload()}
            startIcon={<BrowserUpdatedIcon />}
          >
            Export All Data
          </Button>
        ),
        "history": (
          <Button
            onClick={() => handlehistory()}
            startIcon={<HistoryIcon />}
          >
            Data Log
          </Button>
        ),
        "export_selected": (
          <Button
            disabled={
              !table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()
            }
            onClick={() => handleExportRows(table.getSelectedRowModel().rows)}
            startIcon={<FileDownloadIcon />}
          >
            Export Selected Rows
          </Button>
        ),
        "delete_selected": hasSelection ? (
          <Button
            style={{ color: "red" }}
            onClick={() => handleBulkDelete(table.getSelectedRowModel().rows)}
            startIcon={<DeleteIcon />}
          >
            Delete Selected ({table.getSelectedRowModel().rows.length})
          </Button>
        ) : null,
        "reset": (
          <ResetTablePreferencesButton onOk={() => resetTableState()} />
        ),
      };

      return (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <Box
            className="table-download__wrapper"
            sx={{
              display: "flex",
              gap: "6px",
              padding: "0px",
              flexWrap: "wrap",
            }}
          >
            <SortableContext
              items={currentActionButtonOrder}
              strategy={horizontalListSortingStrategy}
            >
              {currentActionButtonOrder.map((id) => (
                buttonsMap[id] && buttonsConfig[id] && actionButtonVisibility[id] !== false ? (
                  <SortableActionButton key={id} id={id} tooltipText={buttonsConfig[id].tooltipText}>
                    {buttonsMap[id]}
                  </SortableActionButton>
                ) : null
              ))}
            </SortableContext>
          </Box>
        </DndContext>
      );
    },

    renderToolbarInternalActions: ({ table }) => {
      const hasSelection = table.getIsSomeRowsSelected() || table.getIsAllRowsSelected();
      const inactiveIds = hasSelection ? [] : ["delete_selected"];

      return (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <TableActionVisibilityMenu
            actionButtonOrder={currentActionButtonOrder}
            setActionButtonOrder={setActionButtonOrder}
            actionButtonVisibility={actionButtonVisibility}
            setActionButtonVisibility={setActionButtonVisibility}
            buttonsConfig={buttonsConfig}
            defaultOrder={defaultButtonOrder}
            inactiveIds={inactiveIds}
          />
          <MRT_ToggleFiltersButton table={table} />
          <TableColumnVisibilityMenu table={table} />
          <MRT_ToggleDensePaddingButton table={table} />
          <MRT_ToggleFullScreenButton table={table} />
        </Box>
      );
    },

    muiTableHeadCellProps: {
      sx: {
        border: "1px solid #e0e0e087",
        fontStyle: "normal",
        fontWeight: "normal",
        padding: "8px 12px",
      },
    },
    muiTableBodyCellProps: {
      sx: {
        border: "1px solid #e0e0e087",
        padding: "8px 12px",
        fontSize: "0.85rem",
        "&:last-of-type": {
          minWidth: "220px",
        },
      },
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        borderRadius: "0",
        border: "0",
      },
    },
    localization: {
      filterCustomFilterFn: "Custom Filter Fn",
      toggleDensity: `Toggle Density (${density})`,
    } as any,
    renderRowActions: ({ row, table }) => (
      <Box sx={{ display: "flex", gap: 1 }}>
        <Tooltip title="Edit">
          <IconButton onClick={() => table.setEditingRow(row)}>
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton color="error" onClick={() => openDeleteConfirmModal(row)}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Copy Row">
          <IconButton onClick={() => handleCopyRow(row)}>
            <ContentCopyIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="History">
          <IconButton onClick={() => openrowhistory(row)}>
            <HistoryIcon />
          </IconButton>
        </Tooltip>
      </Box>
    ),

    state: {
      columnFilters,
      columnVisibility,
      columnOrder,
      columnPinning,
      columnSizing,
      density,
      globalFilter,
      grouping,
      pagination,
      sorting,
      showColumnFilters,
      showGlobalFilter,
      isFullScreen,
      isLoading: isLoadingUsers,
      isSaving: isCreatingUser || isUpdatingUser || isDeletingUser,
      showAlertBanner: isLoadingUsersError,
      showProgressBars: isFetchingUsers,
    },
  });

  useEffect(() => {
    const transformedFilters = (columnFilters as MRT_ColumnFiltersState).map((filter) => ({
      ...filter,
      mode: (table.getColumn(filter.id)?.getFilterFn() as any)?.name || "contains",
    }));

    setFilterModes(transformedFilters);
  }, [columnFilters, table]);

  return (
    <div className="page-body">
      <Container fluid>
        <Row>
          <div>
            <div className="table-ad">
              <Accordion defaultActiveKey="0" className="table-ad__addInputs">
                <Accordion.Item eventKey="0">
                  <Accordion.Header className="accordion_button">
                    Add New Derivative
                  </Accordion.Header>
                  <Accordion.Body>
                    <Form onSubmit={formCreate}>
                      <div className="form_contain">
                        <div
                          className={`flex formWrapper ${
                            inputBackgroundColour !== "" ? "inputboxcustom" : ""
                          }`}
                          style={{ backgroundColor: inputBackgroundColour }}
                        >
                          <Box
                            sx={{
                              width: "100%",
                              p: { xs: 1, sm: 1 },
                              pt: "20px !important",
                              backgroundColor: "#ffff",
                              borderRadius: "4px",
                              border: "1px solid #e0e0e0",
                            }}
                          >
                            <Box
                              className="form_default"
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                alignItems: "flex-start",
                                justifyContent: "start",
                                gap: 0.5,
                                "& > *": {
                                  minWidth: {
                                    xs: "100%",
                                    sm: "48%",
                                    md: "23%",
                                    lg: "15%",
                                    xl: "7.5%",
                                  },
                                },
                              }}
                            >
                              <CompactTextField
                                label="Enter Derivative"
                                value={derivativeName}
                                onChange={(value) => setDerivativeName(value)}
                                name="derivativeName"
                                error={Boolean(
                                  formErrors.derivativeName &&
                                    touched.derivativeName
                                )}
                                validateOnBlur={validateOnBlur}
                                slotProps={{
                                  inputLabel: {
                                    shrink: true,
                                  },
                                }}
                                onFocus={() => handleFocus("derivativeName")}
                                sx={{
                                  maxWidth: { sm: "250px", xs: "100%" },
                                }}
                              />
                              <Button
                                variant="contained"
                                fullWidth
                                type="submit"
                                sx={{
                                  height: { xs: "36px", sm: "32px" },
                                  flex: { xs: "1 1 auto", sm: "0 0 auto" },
                                  minWidth: { xs: "100%", sm: "100px" },
                                  maxWidth: { sm: "150px" },
                                  backgroundColor: "#00bcd4",
                                  textTransform: "none",
                                  fontSize: "0.75rem",
                                  "&:hover": {
                                    backgroundColor: "#008c9e",
                                  },
                                }}
                              >
                                Add
                              </Button>
                            </Box>
                          </Box>
                        </div>
                      </div>
                    </Form>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
              <div
                className="table-ad__wrapper"
                onContextMenu={handleContextMenu}
              >
                <div className="table-ad__wrapperCard">
                  <MaterialReactTable table={table} />
                  <Menu
                    open={contextMenu !== null}
                    onClose={handleClose}
                    anchorReference="anchorPosition"
                    anchorPosition={
                      contextMenu !== null
                        ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                        : undefined
                    }
                  >
                    <MenuItem onClick={handleResizing}>
                      {showRowResizing ? "Disable Resizing" : "enable Resizing"}
                    </MenuItem>
                  </Menu>
                </div>
                <FileUpload
                  open={openFileUpload}
                  onFileUploadClose={handleFileUploadClose}
                  onFileUploadSubmit={handleFileUploadSubmit}
                  acceptedFileTypes=".csv"
                />
              </div>
            </div>
          </div>
        </Row>
      </Container>
    </div>
  );
};

function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (user: User) => {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      return Promise.resolve();
    },
    onMutate: (newUserInfo: User) => {
      queryClient.setQueryData(
        ["users"],
        (prevUsers: any) =>
          [
            ...prevUsers,
            {
              ...newUserInfo,
              id: (Math.random() + 1).toString(36).substring(7),
            },
          ] as User[]
      );
    },
  });
}

function useGetData(
  filterModes: { mode: string; id: string; value: unknown }[],
  pagination: { pageIndex: number; pageSize: number },
  setTotalCount: any,
  sorting: any
) {
  return useQuery<{ results: User[]; totalCount: number }>({
    queryKey: ["users", filterModes, pagination, sorting],
    queryFn: async () => {
      let url = `${backendbaseUrl}/paper-app/derivatives/?page=${
        pagination.pageIndex + 1
      }&page_size=${pagination.pageSize}`;

      if (filterModes.length > 0) {
        console.log("filterModes---,", filterModes);
        const queryParams = filterModes
          .map(
            (e) =>
              `fieldName__d39d26f3-8442-44d0-8a7f-66cfcbb2c781__${
                e.id
              }=${String(e.value)}__d39d26f3-8442-44d0-8a7f-66cfcbb2c781__${
                e.mode
              }`
          )
          .join("&");

        url += `&${queryParams}`;
      }
      console.log("sortparams:", sorting);
      if (sorting.length > 0) {
        const sortParams = `sortField_${sorting[0].id}=${sorting[0].desc}_sorting`;
        url += `&${sortParams}`;
        console.log("yesss");
      } else {
        console.log("sortParams");
      }

      const response = await axiosInstance.get(url);
      console.log(response.data);
      setTotalCount(response.data.count);
      return {
        results: response.data.results as User[],
        totalCount: response.data.count as number,
      };
    },
    refetchOnWindowFocus: false,
  });
}

function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (user: User) => {
      console.group("🔄 User Update Mutation");
      console.log("Input User Object:", user);

      if (!user || !user.id) {
        console.error("❌ Invalid user object:", user);
        throw new Error("User object is invalid or missing ID");
      }

      const payload = user;

      console.log("📦 Prepared Payload:", payload);

      console.groupEnd();
      try {
        const { data } = await axiosInstance.put(
          `/paper-app/derivatives/${payload.id}/`,
          user
        );
        console.log("✅ API Response:", data);
        console.groupEnd();
        return data;
      } catch (error) {
        console.error("❌ API Request Failed:", error);
        throw error;
      }
    },
    onMutate: async (newUserInfo: User) => {
      console.log("🔮 Optimistic Update - New User Info:", newUserInfo);
      await queryClient.cancelQueries({ queryKey: ["users"] });
      const previousUsers = queryClient.getQueryData(["users"]);
      queryClient.setQueryData(["users"], (oldData: any) =>
        oldData?.map((prevUser: User) =>
          prevUser.id === newUserInfo.id ? newUserInfo : prevUser
        )
      );
      return { previousUsers };
    },
    onError: (err, newUser, context) => {
      console.error("❌ Update Failed:", err);
      queryClient.setQueryData(["users"], context?.previousUsers);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
}

function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      await axiosInstance.delete(
        `${backendbaseUrl}/paper-app/derivatives/${userId}/`
      );
    },
    onMutate: async (userId: string) => {
      await queryClient.cancelQueries({ queryKey: ["users"] });
      const previousUsers = queryClient.getQueryData<User[]>(["users"]);
      queryClient.setQueryData(["users"], (oldUsers: User[] | undefined) =>
        oldUsers?.filter((user) => user.id !== userId)
      );
      return { previousUsers };
    },
    onError: (err, userId, context) => {
      console.error("Error deleting derivative:", err);
      if (context?.previousUsers) {
        queryClient.setQueryData(["users"], context.previousUsers);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
}

const ReactQueryDevtoolsProduction = lazy(() =>
  import("@tanstack/react-query-devtools/build/modern/production.js").then(
    (d) => ({
      default: d.ReactQueryDevtools,
    })
  )
);

const queryClient = new QueryClient();

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Example />
      {/* <Suspense fallback={null}>
        <ReactQueryDevtoolsProduction />
      </Suspense> */}
    </QueryClientProvider>
  );
}

const validateRequired = (value: string) => !!value.length;
const validateEmail = (email: string) =>
  !!email.length &&
  email
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );

function validateUser(user: User) {
  return {};
}
